import React from 'react';
import domains from '../../data/domains';

/**
 * IndustryAssessmentPreview component
 * Showcases the upcoming skills-to-jobs mapping functionality and industry recommendations
 */
const IndustryAssessmentPreview = () => {
  // Get domain color by ID
  const getDomainColor = (domainId) => {
    const domain = domains.find(d => d.id === domainId);
    return domain ? domain.color : '#6c757d';
  };

  // Mock data for skills
  const mockSkills = [
    { id: 'analytical_thinking', name: 'Analytical Thinking', domain: 'insight' },
    { id: 'strategic_planning', name: 'Strategic Planning', domain: 'vision' },
    { id: 'team_collaboration', name: 'Team Collaboration', domain: 'connection' },
    { id: 'process_optimization', name: 'Process Optimization', domain: 'structure' },
    { id: 'leadership', name: 'Leadership', domain: 'impact' },
    { id: 'creative_problem_solving', name: 'Creative Problem Solving', domain: 'vision' },
    { id: 'data_analysis', name: 'Data Analysis', domain: 'insight' },
    { id: 'communication', name: 'Communication', domain: 'connection' },
  ];

  // Mock data for industries
  const mockIndustries = [
    { id: 'technology', name: 'Technology', primaryDomain: 'insight', secondaryDomain: 'vision' },
    { id: 'healthcare', name: 'Healthcare', primaryDomain: 'connection', secondaryDomain: 'structure' },
    { id: 'finance', name: 'Finance', primaryDomain: 'structure', secondaryDomain: 'insight' },
  ];

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your interest! We will contact you when the Industry Assessment is ready.');
  };

  return (
    <div className="industry-assessment-preview">
      <div className="hero-section">
        <div className="hero-content">
          <h1>The Next Evolution in Career Assessment</h1>
          <p className="subtitle">
            Connect your career preferences to real-world skills and industry-specific roles
          </p>
          <div className="coming-soon-badge">Coming Soon</div>
        </div>
      </div>

      <div className="overview-section-blue">
        <h2 className="section-title">What's Coming: Skills-to-Jobs Mapping</h2>
        <p className="section-description">
          We're building a comprehensive system that connects your natural preferences to specific skills
          and industry-relevant job roles, creating a clear pathway from self-understanding to career opportunities.
        </p>

        <div className="feature-cards">
          <div className="feature-card">
            <div className="feature-icon" style={{ backgroundColor: `${getDomainColor('insight')}20` }}>🧠</div>
            <h3>Skills Mapping</h3>
            <p>
              Discover which workplace skills naturally align with your preference pattern, with clear connections
              to your psychological domains and top preferences.
            </p>
            <div className="skill-tags-preview">
              {mockSkills.slice(0, 4).map(skill => (
                <span 
                  key={skill.id} 
                  className="skill-tag"
                  style={{ 
                    backgroundColor: `${getDomainColor(skill.domain)}15`,
                    color: getDomainColor(skill.domain),
                    borderLeft: `3px solid ${getDomainColor(skill.domain)}`
                  }}
                >
                  {skill.name}
                </span>
              ))}
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon" style={{ backgroundColor: `${getDomainColor('vision')}20` }}>🌐</div>
            <h3>Industry Recommendations</h3>
            <p>
              Receive personalized industry recommendations based on your preference pattern and domain strengths,
              with insights into why certain industries are better matches.
            </p>
            <div className="industry-cards-preview">
              {mockIndustries.map(industry => (
                <div 
                  key={industry.id} 
                  className="industry-card"
                  style={{ 
                    borderTop: `3px solid ${getDomainColor(industry.primaryDomain)}`,
                    borderBottom: `3px solid ${getDomainColor(industry.secondaryDomain)}`
                  }}
                >
                  {industry.name}
                </div>
              ))}
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon" style={{ backgroundColor: `${getDomainColor('structure')}20` }}>📋</div>
            <h3>Job Role Library</h3>
            <p>
              Explore a comprehensive library of job roles organized by industry, with detailed information about
              required skills and preference alignment.
            </p>
            <div className="job-preview">
              <div className="job-preview-header">
                <span>Sample Job Roles</span>
              </div>
              <ul className="job-preview-list">
                <li>Data Scientist</li>
                <li>UX Researcher</li>
                <li>Project Manager</li>
                <li>Strategic Consultant</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="overview-section-blue">
        <h2 className="section-title">How It Will Work</h2>
        
        <div className="process-steps">
          <div className="process-step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3>Complete Your Preference Assessment</h3>
              <p>
                First, complete the Career Preferences Assessment to identify your natural work preferences
                and domain strengths.
              </p>
            </div>
          </div>
          
          <div className="process-step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3>Take the Industry Assessment</h3>
              <p>
                Next, take a supplementary assessment focused on industry-specific interests and preferences
                to refine your recommendations.
              </p>
            </div>
          </div>
          
          <div className="process-step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3>Explore Your Industry Matches</h3>
              <p>
                Receive personalized industry recommendations with detailed explanations of alignment with your
                preference pattern.
              </p>
            </div>
          </div>
          
          <div className="process-step">
            <div className="step-number">4</div>
            <div className="step-content">
              <h3>Discover Aligned Job Roles</h3>
              <p>
                Browse through job roles within your matched industries, with skill requirements and preference
                alignment clearly highlighted.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="overview-section-blue">
        <h2 className="section-title">Get Early Access</h2>
        <p className="section-description">
          Be among the first to experience the Industry Assessment and Skills-to-Jobs Mapping when it launches.
          Sign up below to reserve your spot and receive updates on our progress.
        </p>
        
        <div className="signup-card">
          <h3>Reserve Your Spot</h3>
          <p>
            Complete the form below to join our waitlist. We'll contact you as soon as the Industry Assessment
            is ready for testing.
          </p>
          
          <form onSubmit={handleSubmit} className="signup-form">
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input type="text" id="name" name="name" required />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input type="email" id="email" name="email" required />
            </div>
            
            <div className="form-group checkbox-group">
              <input type="checkbox" id="consent" name="consent" required />
              <label htmlFor="consent">
                I consent to being contacted by email when the Industry Assessment is available for testing.
              </label>
            </div>
            
            <button type="submit" className="submit-button">
              Join the Waitlist
            </button>
          </form>
        </div>
      </div>

      <style jsx>{`
        .industry-assessment-preview {
          font-family: system-ui, -apple-system, sans-serif;
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .hero-section {
          background: linear-gradient(135deg, #54548E 0%, #776DC7 100%);
          border-radius: 12px;
          color: white;
          padding: 3rem 2rem;
          text-align: center;
          margin-bottom: 1.5rem;
          position: relative;
          overflow: hidden;
        }

        .hero-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='white' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E");
          opacity: 0.2;
        }

        .hero-content {
          position: relative;
          z-index: 1;
        }

        .hero-section h1 {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 1rem;
          line-height: 1.2;
        }

        .subtitle {
          font-size: 1.25rem;
          margin-bottom: 1.5rem;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
        }

        .coming-soon-badge {
          display: inline-block;
          background-color: rgba(255, 255, 255, 0.15);
          padding: 0.5rem 1.25rem;
          border-radius: 30px;
          font-weight: 600;
          font-size: 1rem;
          backdrop-filter: blur(4px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .overview-section-blue {
          background-color: #f8fafc;
          border-radius: 8px;
          padding: 2rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          margin-bottom: 1.5rem;
        }

        .section-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.6rem;
          text-align: left;
        }

        .section-description {
          color: #64748b;
          margin-bottom: 1.8rem;
          text-align: left;
          font-size: 1.1rem;
          line-height: 1.5;
        }

        .feature-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          margin-bottom: 1rem;
        }

        .feature-card {
          background-color: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .feature-icon {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.75rem;
          margin-bottom: 1rem;
        }

        .feature-card h3 {
          font-size: 1.35rem;
          font-weight: 700;
          color: #1e293b;
          margin-top: 0;
          margin-bottom: 0.75rem;
        }

        .feature-card p {
          color: #475569;
          font-size: 1rem;
          line-height: 1.5;
          margin-bottom: 1.5rem;
          flex-grow: 1;
        }

        .skill-tags-preview {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .skill-tag {
          padding: 0.3rem 0.75rem;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          white-space: nowrap;
        }

        .industry-cards-preview {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 0.75rem;
        }

        .industry-card {
          background-color: white;
          padding: 0.75rem;
          border-radius: 8px;
          text-align: center;
          font-weight: 600;
          font-size: 0.875rem;
          color: #1e293b;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .job-preview {
          background-color: #f8fafc;
          border-radius: 8px;
          overflow: hidden;
        }

        .job-preview-header {
          background-color: #e2e8f0;
          padding: 0.5rem 1rem;
          font-weight: 600;
          font-size: 0.875rem;
          color: #475569;
        }

        .job-preview-list {
          list-style: none;
          padding: 0.75rem 1rem;
          margin: 0;
        }

        .job-preview-list li {
          padding: 0.5rem 0;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.875rem;
          color: #334155;
        }

        .job-preview-list li:last-child {
          border-bottom: none;
        }

        .process-steps {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          margin-top: 2rem;
        }

        .process-step {
          display: flex;
          background-color: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          align-items: flex-start;
          gap: 1.25rem;
        }

        .step-number {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background-color: #F2682A;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.125rem;
          flex-shrink: 0;
        }

        .step-content {
          flex: 1;
        }

        .step-content h3 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          margin-top: 0;
          margin-bottom: 0.5rem;
        }

        .step-content p {
          color: #475569;
          font-size: 1rem;
          line-height: 1.5;
          margin: 0;
        }

        .signup-card {
          background-color: white;
          border-radius: 12px;
          padding: 2rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          margin-top: 1.5rem;
        }

        .signup-card h3 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
          margin-top: 0;
          margin-bottom: 1rem;
        }

        .signup-card p {
          color: #475569;
          font-size: 1.125rem;
          line-height: 1.5;
          margin-bottom: 1.5rem;
        }

        .signup-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          font-weight: 500;
          color: #334155;
          font-size: 0.9375rem;
        }

        .form-group input[type="text"],
        .form-group input[type="email"] {
          padding: 0.75rem;
          border: 1px solid #cbd5e1;
          border-radius: 6px;
          font-size: 1rem;
          transition: border-color 0.2s;
        }

        .form-group input[type="text"]:focus,
        .form-group input[type="email"]:focus {
          border-color: #54548E;
          outline: none;
          box-shadow: 0 0 0 2px rgba(84, 84, 142, 0.2);
        }

        .checkbox-group {
          flex-direction: row;
          align-items: flex-start;
          gap: 0.75rem;
        }

        .checkbox-group input[type="checkbox"] {
          margin-top: 0.25rem;
        }

        .checkbox-group label {
          font-size: 0.9375rem;
          color: #475569;
          font-weight: 400;
        }

        .submit-button {
          background-color: #F2682A;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 0.875rem 1.5rem;
          font-size: 1.125rem;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s;
          margin-top: 0.5rem;
        }

        .submit-button:hover {
          background-color: #d14e1c;
        }

        @media (max-width: 768px) {
          .hero-section {
            padding: 2rem 1.5rem;
          }

          .hero-section h1 {
            font-size: 2rem;
          }

          .subtitle {
            font-size: 1.125rem;
          }

          .overview-section-blue {
            padding: 1.5rem;
          }

          .feature-cards {
            grid-template-columns: 1fr;
          }

          .process-step {
            padding: 1.25rem;
          }

          .signup-card {
            padding: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default IndustryAssessmentPreview; 
